'use client';

import { useState, useRef, useEffect } from 'react';
import { FiClock, FiChevronDown } from 'react-icons/fi';
import { useTourStore } from '@eliadmin/core/src/stores/tours-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoClose } from 'react-icons/io5';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { TourSidebarFilterLabel } from '../tour-sidebar-filter-label';

export const TourSidebarFilterNightsCount = () => {
  const { filter, setFilter } = useTourStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslations();

  const nightsCountOptions = [
    { label: t('text1843'), value: 3 },
    { label: t('text1844'), value: 6 },
    { label: t('text1845'), value: 15 },
    { label: t('text1846'), value: 16 },
  ];

  const handleNightsChange = (value: number | undefined) => {
    setFilter({
      ...filter,
      nights_count: value,
    });
    setIsOpen(false); // Close dropdown after selection
  };

  const getSelectedLabel = () => {
    const selectedOption = nightsCountOptions.find(option => option.value === filter.nights_count);
    return selectedOption ? selectedOption.label : t('text1847');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="text-sm font-semibold">
      <TourSidebarFilterLabel label={t('text1853')} />
      <div ref={dropdownRef} className="relative">
        {/* Main Dropdown Trigger */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-12 min-w-[140px] cursor-pointer items-center justify-between rounded-md border border-[#e5e7eb] bg-white px-3"
        >
          <div className="flex items-center space-x-1">
            <FiClock className="mr-1 h-5 w-5 text-primary" />
            <span className="whitespace-nowrap text-base font-normal text-gray-800">
              {getSelectedLabel()}
            </span>
            {!!filter.nights_count && (
              <IoClose
                onClick={() => handleNightsChange(undefined)}
                className="ml-2 h-4 w-4 cursor-pointer text-destructive-foreground"
              />
            )}
          </div>
          <FiChevronDown
            className={cn('h-4 w-4 text-gray-500 text-primary', !filter.nights_count && 'ml-2')}
          />
        </div>

        {/* Dropdown Options */}
        {isOpen && (
          <div className="absolute  left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            {nightsCountOptions.map(option => (
              <div
                key={option.value}
                onClick={() => handleNightsChange(option.value)}
                className="cursor-pointer px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
